// Centralised content for the marketing site so it's easy to edit in one place.

export const BRAND = {
  name: "Declan Bishop",
  role: "Property Consultant",
  full: "Declan Bishop — Property Consultant",
  logoUrl: "/logo.png",
  phone: "07947 981 727",
  email: "bishop_declan@icloud.com",
  serviceAreas: ["Lincolnshire"],
  serviceAreaLabel: "Serving Lincolnshire & surrounding areas",
  addressLines: ["Lincolnshire", "United Kingdom"],
  hours: "Mon – Fri · 08:30 – 18:00",
};

// Towns served — used in copy, sitemap and JSON-LD schema for local SEO
export const SERVICE_TOWNS = [
  "Lincoln",
  "Sleaford",
  "Boston",
  "Horncastle",
  "Louth",
  "Spilsby",
  "Alford",
  "Skegness",
  "Ingoldmells",
  "Chapel St Leonards",
  "Woodhall Spa",
  "Tattershall",
  "Coningsby",
  "Wragby",
  "Market Rasen",
  "Caistor",
  "Spalding",
  "Holbeach",
  "Sutton Bridge",
  "Bourne",
  "Market Deeping",
  "Crowland",
  "Grantham",
  "Newark-on-Trent",
];

export const SERVICE_TOWNS_BY_AREA = [
  {
    heading: "Central Lincolnshire",
    towns: ["Lincoln", "Wragby", "Market Rasen", "Caistor"],
  },
  {
    heading: "East Coast & Wolds",
    towns: [
      "Skegness",
      "Ingoldmells",
      "Chapel St Leonards",
      "Alford",
      "Louth",
      "Spilsby",
      "Horncastle",
      "Woodhall Spa",
      "Tattershall",
      "Coningsby",
    ],
  },
  {
    heading: "South Lincolnshire & The Fens",
    towns: [
      "Sleaford",
      "Boston",
      "Spalding",
      "Holbeach",
      "Sutton Bridge",
      "Crowland",
      "Market Deeping",
      "Bourne",
    ],
  },
  {
    heading: "South West & Borders",
    towns: ["Grantham", "Newark-on-Trent"],
  },
];

export const CREDENTIALS = [
  {
    code: "AssocRICS",
    label: "Associate of the Royal Institution of Chartered Surveyors",
    description:
      "Globally recognised standard of professional practice in surveying, valuation and property advice.",
  },
  {
    code: "MCIOB",
    label: "Member of the Chartered Institute of Building",
    description:
      "Chartered status confirming expertise across construction quality, defects and building performance.",
  },
  {
    code: "BDMA.Tech",
    label: "British Damage Management Association — Technician",
    description:
      "Specialist accreditation in moisture, damp, and building damage diagnosis and remediation.",
  },
];

export const SERVICES = [
  {
    slug: "domestic-epcs",
    number: "01",
    title: "Domestic EPCs",
    short:
      "Accredited Energy Performance Certificates for sales, lettings and renovations.",
    long:
      "A Domestic EPC is the legally-required Energy Performance Certificate produced whenever a home is sold or let. Each visit captures construction, insulation, glazing, heating and lighting data and is modelled against the SAP methodology, returning an accurate A–G rating with prioritised improvement recommendations.",
    benefits: [
      "Lodged EPC, accepted by HMLR, lenders and letting agents",
      "Clear A–G rating with cost-saving improvement notes",
      "Same-week turnaround across Lincolnshire",
      "Discreet, single-visit inspection by an accredited assessor",
    ],
    audience: ["Homeowners", "Landlords", "Estate Agents"],
  },
  {
    slug: "landlord-inspection-reports",
    number: "02",
    title: "Landlord Inspection Reports",
    short:
      "Mid-term and check-out reports that protect rental income, deposits and compliance.",
    long:
      "A detailed, photographed audit of the property during or at the end of a tenancy. Each report records condition room-by-room, flags maintenance issues, identifies tenant damage versus fair wear and tear, and supports deposit deductions under TDS, DPS and MyDeposits adjudication.",
    benefits: [
      "Independent, dated evidence for deposit disputes",
      "Room-by-room condition log with photography",
      "Compliance flags: smoke, CO, electrical, damp",
      "Recommendations to protect long-term asset value",
    ],
    audience: ["Landlords", "Letting Agents", "Portfolio Owners"],
  },
  {
    slug: "property-condition-surveys",
    number: "03",
    title: "Property Condition Surveys",
    short:
      "RICS-aligned condition reports giving a clear picture of a property's state and risks.",
    long:
      "Following the RICS Home Survey Standard, a Property Condition Survey delivers a traffic-light assessment of the building's elements: roof, walls, services, joinery, drainage and grounds. Ideal for conventional homes in reasonable order, it surfaces urgent matters, legal queries and onward maintenance priorities without overwhelming the reader.",
    benefits: [
      "Traffic-light ratings against RICS guidance",
      "Plain-English commentary on every major element",
      "Urgent defects and legal matters flagged early",
      "Confidence to negotiate, proceed or walk away",
    ],
    audience: ["Property Buyers", "Homeowners", "Estate Agents"],
  },
  {
    slug: "new-build-snagging-surveys",
    number: "04",
    title: "New Build Snagging Surveys",
    short:
      "Detailed pre-completion snagging to capture every defect before your developer hands over.",
    long:
      "A forensic walk-through of your new home, capturing every cosmetic flaw, finish defect and build issue before — or shortly after — legal completion. Each item is photographed, located by room, categorised by severity, and presented in a developer-ready schedule so your housebuilder can resolve them under their warranty.",
    benefits: [
      "Hundreds of items checked across structure, finish and services",
      "Developer-ready snag schedule with photographs",
      "Carried out before completion or within the warranty window",
      "Helps you hold the developer to their build quality promise",
    ],
    audience: ["New Build Buyers", "Self-Build Clients"],
  },
  {
    slug: "defect-reporting",
    number: "05",
    title: "Defect Reporting",
    short:
      "Targeted reports on damp, disrepair, damage and specific concerns at a property.",
    long:
      "Focused reporting for clients who need clarity on a single, specific issue — recurring damp, suspected subsidence, post-storm damage, disrepair claims or insurance queries. Combines on-site investigation with diagnostic tools (moisture, thermal, structural review) and a written report suitable for solicitors, insurers and lenders.",
    benefits: [
      "Focused investigation of the issue that matters",
      "Diagnostic tools: moisture, thermal, structural",
      "Reports written for solicitors, insurers and lenders",
      "Independent, unbiased professional opinion",
    ],
    audience: ["Homeowners", "Buyers", "Solicitors", "Insurers"],
  },
  {
    slug: "energy-efficiency-advice",
    number: "06",
    title: "Energy Efficiency Advice",
    short:
      "Tailored guidance on lifting your EPC rating and lowering running costs.",
    long:
      "A practical, on-site review of how a property uses energy — fabric, services, controls and behaviour — and a costed plan to improve it. We translate the EPC into real-world steps: which measures matter, which are best value, and which qualify for incentives or grant schemes.",
    benefits: [
      "Prioritised, costed improvement plan",
      "Roadmap from current rating to target EPC band",
      "Guidance on grants, ECO4 and funding routes",
      "Advice independent of any supplier or installer",
    ],
    audience: ["Homeowners", "Landlords", "Property Buyers"],
  },
  {
    slug: "floor-plans-photo-pack",
    number: "07",
    title: "Floor Plans / Photo Pack",
    short:
      "Sales-ready floor plans and professional photography for agents and private sellers.",
    long:
      "Marketing-grade floor plans drawn from on-site laser measurement, paired with a curated set of high-resolution interior and exterior photographs. Designed for property listings, lettings, planning submissions and architectural reference — delivered as print- and portal-ready files.",
    benefits: [
      "Accurate, dimensioned floor plans (laser-measured)",
      "Polished photography optimised for portals",
      "Portal- and print-ready file formats",
      "Faster, more confident enquiries from buyers and tenants",
    ],
    audience: ["Estate Agents", "Landlords", "Private Sellers"],
  },
  {
    slug: "roof-surveys",
    number: "08",
    title: "Roof Surveys",
    short:
      "Specialist inspections of roof condition, covering, structure and weathering details.",
    long:
      "A focused inspection of pitched and flat roofs — covering tiles or slates, flashings, gutters, fascias, soffits, chimneys and visible structure. Reports highlight defects, life-expectancy, weather-tightness and any work needed to keep the roof performing, supported by photography from ground or elevated vantage points where safe.",
    benefits: [
      "Specialist focus on a high-value building element",
      "Defect, life-expectancy and repair-priority assessment",
      "Photographic evidence from safe vantage points",
      "Clear scope: repair, overhaul or full replacement",
    ],
    audience: ["Homeowners", "Buyers", "Landlords", "Insurers"],
  },
  {
    slug: "rics-home-surveys",
    number: "09",
    title: "RICS Home Surveys (Levels 1–3)",
    short:
      "RICS Home Surveys at Levels 1, 2 and 3 — survey only, from snapshot to in-depth building analysis.",
    long:
      "RICS Home Surveys to the current Home Survey Standard, at all three levels: Level 1 for modern conventional homes, Level 2 (formerly the HomeBuyer Report) for most houses, bungalows and flats, and Level 3 (formerly the Building Survey) for older, altered or complex properties. Provided on a survey-only basis — valuations are not included.",
    benefits: [
      "All three RICS levels — honest advice on which you actually need",
      "Traffic-light ratings, photographed evidence, plain-English reporting",
      "Level 3 includes defect analysis, repair options and consequences",
      "Follow-up call with every survey to walk through the findings",
    ],
    audience: ["Home Buyers", "Homeowners", "Estate Agents"],
  },
];

export const WHY_POINTS = [
  {
    n: "01",
    title: "Independent & Impartial",
    body:
      "No agency ties, no commission. Just a clear professional opinion you can rely on when the stakes are high.",
  },
  {
    n: "02",
    title: "Triple-Accredited",
    body:
      "AssocRICS, MCIOB and BDMA.Tech — qualifications spanning surveying, construction quality and damage diagnostics.",
  },
  {
    n: "03",
    title: "Plain-English Reporting",
    body:
      "Reports written for the people who need to use them — buyers, landlords and solicitors — not just other surveyors.",
  },
  {
    n: "04",
    title: "Locally Focused",
    body:
      "Specialist knowledge of Lincolnshire property stock — from Georgian townhouses and Wolds farmsteads to modern coastal and Fens developments.",
  },
];

export const PROCESS_STEPS = [
  {
    step: "I",
    title: "Enquiry",
    body: "Tell us about the property and what you need. We confirm scope, fee and a visit window — usually within 48 hours.",
  },
  {
    step: "II",
    title: "Site Inspection",
    body: "Methodical on-site inspection using accredited tools and survey checklists tailored to the service.",
  },
  {
    step: "III",
    title: "Report",
    body: "A photographed, plain-English written report delivered to your inbox — typically within 3–5 working days.",
  },
  {
    step: "IV",
    title: "Follow-up",
    body: "A short call to walk through findings, answer questions, and discuss any next steps with your solicitor or agent.",
  },
];
